import { Slider as SliderDS } from '@lmn/design-system';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

export type RangeSelectedValues = {
  min: number;
  minAllowed: number;
  max: number;
  maxAllowed: number;
  disabled: boolean;
};

const SliderContainer = styled.div`
  width: 100%;
  border-radius: 8px;
  background-color: white;

  /* Disabled is left to the design system, the one state the design does not cover. */
  [role='slider']:not([data-disabled]) {
    box-sizing: border-box;
    border: 2px solid ${({ theme }) => theme.kallisto.color.border.interactive.borderPrimaryDefault};
  }
`;

export type SliderBound = 'min' | 'max';

export type SliderThumbProps = {
  thumbNames?: Record<SliderBound, string>;
  valueText?: (value: number, bound: SliderBound, atTop: boolean) => string;
};

type SliderProps = SliderThumbProps & {
  value: RangeSelectedValues;
  label?: string;
  disabled: boolean;
  handleChange: (value: number[] | number) => void;
  handleCommittedChange: (value: number[] | number) => void;
  formatter: (v: number) => string;
  hideMin: boolean;
  // Left out, the design system steps by 1, which makes a wide range unusable from the keyboard.
  step?: number;
};

const Slider = ({
  value,
  label,
  disabled,
  handleChange,
  handleCommittedChange,
  formatter,
  hideMin,
  step,
  thumbNames,
  valueText,
}: SliderProps) => {
  const { min, max, minAllowed, maxAllowed } = value;
  const containerRef = useRef<HTMLDivElement>(null);
  const valueTextRef = useRef(valueText);
  valueTextRef.current = valueText;
  const minName = thumbNames?.min ?? '';
  const maxName = thumbNames?.max ?? '';
  const describesThumbs = !!thumbNames || !!valueText;

  // The DS Slider takes no per-thumb props, so the thumbs' names and value texts are set on its nodes (min thumb
  // first). Remove this once it forwards per-thumb aria-label / aria-valuetext. An observer, because the DS copies
  // selectedValue into its own state a render after our props change.
  useEffect(() => {
    const container = containerRef.current;
    if (!describesThumbs || !container) return undefined;

    const setAttribute = (thumb: Element, name: string, next: string) => {
      if (next && thumb.getAttribute(name) !== next) thumb.setAttribute(name, next);
    };

    const sync = () => {
      const thumbs = [...container.querySelectorAll('[role="slider"]')];
      const bounds: SliderBound[] = thumbs.length === 1 ? ['max'] : ['min', 'max'];
      thumbs.forEach((thumb, index) => {
        const bound = bounds[index];
        const now = Number(thumb.getAttribute('aria-valuenow'));
        const atTop = now >= Number(thumb.getAttribute('aria-valuemax'));
        setAttribute(thumb, 'aria-label', bound === 'min' ? minName : maxName);
        const text = valueTextRef.current?.(now, bound, atTop) ?? '';
        if (text) setAttribute(thumb, 'aria-valuetext', text);
        else thumb.removeAttribute('aria-valuetext');
      });
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-valuenow', 'aria-valuemax', 'aria-label'],
    });
    return () => observer.disconnect();
  }, [describesThumbs, minName, maxName]);

  return (
    <SliderContainer ref={containerRef} className="slider" data-swipe-dismiss-ignore={true}>
      <SliderDS
        formatter={formatter}
        rangeMax={maxAllowed}
        rangeMin={minAllowed}
        selectedValue={hideMin ? max : min}
        LabelText={label || ''}
        disabled={disabled}
        handleChange={handleChange}
        handleCommittedChange={handleCommittedChange}
        selectedSecondValue={hideMin ? undefined : max}
        step={step}
      />
    </SliderContainer>
  );
};

export default Slider;
